import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Button } from "@/shared/components/shadcn/Button";
import { Checkbox } from "@/shared/components/shadcn/Checkbox";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { paymentMethodApiService as api } from "../services/paymentMethodApiService";
import {
  fromSetupConsentDtoToSetupConsent, fromSetupDetailsDtoToSetupDetails, fromSetupStatusDtoToSetupStatus,
  type SetupConsent, type SetupDetails, type SetupStatus,
} from "../types/paymentMethodTypes";

const StripeCardSetup = lazy(() => import("./StripeCardSetup"));

export function CardSetupFlow({ token, onDone }: { token: string; onDone?: () => void }) {
  const [details, setDetails] = useState<SetupDetails>();
  const [setup, setSetup] = useState<SetupConsent>();
  const [status, setStatus] = useState<SetupStatus>();
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [checking, setChecking] = useState(false);
  const [confirmationAttempted, setConfirmationAttempted] = useState(false);
  const controller = useRef<AbortController | null>(null);
  const submitting = useRef(false);
  const returnUrl = `${window.location.origin}/payment-methods/setup#token=${encodeURIComponent(token)}`;

  useEffect(() => {
    const current = new AbortController();
    controller.current = current;
    async function load() {
      try {
        const info = fromSetupDetailsDtoToSetupDetails(await api.open(token, current.signal));
        const result = fromSetupStatusDtoToSetupStatus(await api.status(token, current.signal));
        if (!current.signal.aborted) { setDetails(info); setStatus(result); }
      } catch {
        if (!current.signal.aborted) setError("This card setup link is unavailable or expired. Try again, or ask the business for a new link.");
      }
    }
    void load();
    return () => current.abort();
  }, [token, attempt]);

  async function checkStatus() {
    const signal = controller.current?.signal;
    if (!signal || signal.aborted) return;
    setChecking(true);
    setError("");
    try {
      const result = fromSetupStatusDtoToSetupStatus(await api.status(token, signal));
      if (!signal.aborted) setStatus(result);
    } catch {
      if (!signal.aborted) setError("Couldn't check whether the card was saved. Refresh status before submitting again.");
    } finally {
      if (!signal.aborted) setChecking(false);
    }
  }
  async function accept() {
    const signal = controller.current?.signal;
    if (!details || !consent || submitting.current || !signal || signal.aborted) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      const result = fromSetupConsentDtoToSetupConsent(await api.consent(token, details.consentVersion, signal));
      if (result.stripeAccountId !== details.stripeAccountId || result.publishableKey !== details.publishableKey) {
        throw new Error("Stripe setup context changed.");
      }
      if (!signal.aborted) setSetup(result);
    } catch {
      if (!signal.aborted) setError("Couldn't start secure card setup. No charge was made. Please try again.");
    } finally {
      submitting.current = false;
      if (!signal.aborted) setBusy(false);
    }
  }
  if (!details || !status) return error
    ? <ErrorState message={error} onRetry={() => { setError(""); setAttempt(value => value + 1); }} />
    : <LoadingSpinner message="Loading card setup..." />;
  if (status.status === "succeeded") return <div className="space-y-4">
    <p role="status" className="font-medium">Card saved. No payment was taken.</p>
    {status.card && <p>{status.card.brand} ending in {status.card.last4}</p>}
    <p className="text-sm text-muted-foreground">Your card is stored by Stripe for {details.businessName}.</p>
    {onDone ? <Button onClick={onDone}>Done</Button> : <p>You can close this page.</p>}
  </div>;
  if (status.status === "canceled") return <ErrorState message="This card setup was canceled. Ask the business for a new link." />;
  return <div className="space-y-4">
    <div>
      <p className="font-medium">{details.businessName}</p>
      <p className="text-sm">Save a card for {details.customerName}</p>
      <p className="text-sm text-muted-foreground">{details.environment === "test" ? "Test mode - use test cards only." : "Live mode"} No payment will be taken.</p>
      <p className="text-xs text-muted-foreground">Link expires {details.expiresAt.toLocaleString()}.</p>
    </div>
    <p className="text-sm text-muted-foreground">The customer must enter their own card details and agree below. Stripe may ask their bank to verify the card.</p>
    <p className="whitespace-pre-wrap text-sm">{details.consentText}</p>
    {!setup ? <>
      <label className="flex items-start gap-2 text-sm">
        <Checkbox checked={consent} onCheckedChange={value => setConsent(value === true)} disabled={busy} />
        <span className="py-2">I am the customer and agree to save my card for the future payments described above.</span>
      </label>
      <Button disabled={!consent || busy} onClick={() => void accept()}>{busy ? "Preparing secure form..." : "Continue to secure card form"}</Button>
    </> : <Suspense fallback={<LoadingSpinner message="Loading secure card form..." />}>
      <StripeCardSetup key={setup.clientSecret} setup={setup} returnUrl={returnUrl} onConfirmed={async () => {
        setConfirmationAttempted(true);
        await checkStatus();
      }} />
    </Suspense>}
    {confirmationAttempted && !checking && !error && <p role="status" className="text-sm">
      Card setup is still pending. Complete any bank verification, then refresh setup status.
    </p>}
    {error && <ErrorState message={error} />}
    <Button variant="quiet" disabled={busy || checking} onClick={() => void checkStatus()}>{checking ? "Checking card setup..." : "Refresh setup status"}</Button>
  </div>;
}
